import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  Receipt,
  Calendar,
  Search,
  Printer,
  RotateCcw,
  FileSpreadsheet,
  User,
  Clock,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { formatTRY } from "@/lib/currency";

const monthNames = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

export const Route = createFileRoute("/_auth/admin/orders")({
  component: OrdersManagementPage,
});

interface MockOrderItem {
  name: string;
  quantity: number;
  price: number;
  note?: string;
}

interface CancelledItem {
  name: string;
  quantity: number;
  price: number;
  cancelledBy: string;
  reason: string;
  time: string;
}

interface PaymentItem {
  method: string;
  amount: number;
}

interface MockOrder {
  id: string;
  table: string;
  waiter: string;
  cashier: string;
  time: string;
  date: string;
  guestCount: number;
  coverCharge: number;
  serviceCharge: number;
  discount: number;
  discountReason?: string;
  tax: number;
  taxRate: number;
  total: number;
  payments: PaymentItem[];
  items: MockOrderItem[];
  cancelledItems: CancelledItem[];
  status: "completed" | "refunded";
  fiscalInfo: {
    receiptNo: string;
    zNo: string;
  };
}

const MOCK_ORDERS: MockOrder[] = [
  {
    id: "AD-8402",
    table: "S-01",
    waiter: "Ahmet Yılmaz",
    cashier: "Ahmet Yılmaz",
    time: "11:30",
    date: "Bugün",
    guestCount: 3,
    coverCharge: 45.0,
    serviceCharge: 0,
    discount: 0,
    taxRate: 10,
    tax: 25.9,
    total: 285.0,
    payments: [
      { method: "Kredi Kartı", amount: 200.0 },
      { method: "Nakit", amount: 85.0 },
    ],
    items: [
      { name: "Caffe Latte (Yulaf Sütü)", quantity: 2, price: 70.0 },
      { name: "San Sebastian Cheesecake", quantity: 1, price: 100.0 },
    ],
    cancelledItems: [
      {
        name: "Espresso Single",
        quantity: 1,
        price: 50.0,
        cancelledBy: "Ahmet Yılmaz",
        reason: "Yanlış sipariş girildi",
        time: "11:05",
      },
    ],
    status: "completed",
    fiscalInfo: {
      receiptNo: "FIŞ-0012",
      zNo: "0142",
    },
  },
  {
    id: "AD-8401",
    table: "B-03",
    waiter: "Mehmet Demir",
    cashier: "Hüseyin Kaya",
    time: "11:15",
    date: "Bugün",
    guestCount: 2,
    coverCharge: 30.0,
    serviceCharge: 38.0,
    discount: 0,
    taxRate: 10,
    tax: 41.6,
    total: 448.0,
    payments: [
      { method: "Nakit", amount: 448.0 },
    ],
    items: [
      { name: "Pepperoni Pizza (Klasik Kenar)", quantity: 1, price: 260.0 },
      { name: "Coca Cola", quantity: 2, price: 60.0 },
    ],
    cancelledItems: [],
    status: "completed",
    fiscalInfo: {
      receiptNo: "FIŞ-0011",
      zNo: "0142",
    },
  },
  {
    id: "AD-8400",
    table: "T-02",
    waiter: "Ayşe Kaya",
    cashier: "Ayşe Kaya",
    time: "10:45",
    date: "Bugün",
    guestCount: 4,
    coverCharge: 60.0,
    serviceCharge: 0,
    discount: 30.0,
    discountReason: "%20 Kahvaltı İndirimi",
    taxRate: 10,
    tax: 16.3,
    total: 180.0,
    payments: [
      { method: "Yemek Kartı", amount: 180.0 },
    ],
    items: [
      { name: "Türk Çayı", quantity: 3, price: 15.0 },
      { name: "Simit & Krem Peynir", quantity: 1, price: 105.0 },
    ],
    cancelledItems: [],
    status: "completed",
    fiscalInfo: {
      receiptNo: "FIŞ-0010",
      zNo: "0142",
    },
  },
  {
    id: "AD-8399",
    table: "S-05",
    waiter: "Ahmet Yılmaz",
    cashier: "Hüseyin Kaya",
    time: "10:20",
    date: "Bugün",
    guestCount: 2,
    coverCharge: 30.0,
    serviceCharge: 0,
    discount: 0,
    taxRate: 10,
    tax: 55.0,
    total: 670.0,
    payments: [
      { method: "Kredi Kartı", amount: 670.0 },
    ],
    items: [
      { name: "Burger Special (Mantar & Karamelize Soğan)", quantity: 2, price: 240.0 },
      { name: "Ev Yapımı Limonata", quantity: 2, price: 80.0 },
    ],
    cancelledItems: [],
    status: "completed",
    fiscalInfo: {
      receiptNo: "FIŞ-0009",
      zNo: "0142",
    },
  },
  {
    id: "AD-8396",
    table: "S-07",
    waiter: "Ahmet Yılmaz",
    cashier: "Hüseyin Kaya",
    time: "19:30",
    date: "Dün",
    guestCount: 2,
    coverCharge: 30.0,
    serviceCharge: 0,
    discount: 0,
    taxRate: 10,
    tax: 31.0,
    total: 340.0,
    payments: [
      { method: "Nakit", amount: 340.0 },
    ],
    items: [
      { name: "Tavuklu Sezar Salata", quantity: 2, price: 155.0 },
    ],
    cancelledItems: [],
    status: "refunded",
    fiscalInfo: {
      receiptNo: "FIŞ-0098",
      zNo: "0141",
    },
  },
];

export function OrdersManagementPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [selectedOrderId, setSelectedOrderId] = useState<string>(MOCK_ORDERS[0]?.id || "");

  // Date range states
  const [startDate, setStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6; // Sunday becomes index 6

    const days: (Date | null)[] = Array(startDayOfWeek).fill(null);
    const totalDays = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= totalDays; d++) {
      days.push(new Date(year, month, d));
    }
    return days;
  }, [calendarMonth]);

  const handleDayClick = (day: Date) => {
    if ((startDate && endDate) || !startDate) {
      setStartDate(day);
      setEndDate(null);
    } else {
      if (day < startDate) {
        setStartDate(day);
        setEndDate(null);
      } else {
        setEndDate(day);
        setIsDatePickerOpen(false);
      }
    }
  };

  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const dateRangeLabel = useMemo(() => {
    if (startDate && endDate) {
      if (startDate.getTime() === endDate.getTime()) {
        return formatDateDisplay(startDate);
      }
      return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
    }
    if (startDate) {
      return `${formatDateDisplay(startDate)} - Seçiliyor...`;
    }
    return "Tarih Seçin";
  }, [startDate, endDate]);

  // Filters calculation
  const filteredOrders = useMemo(() => {
    return MOCK_ORDERS.filter((order) => {
      const matchesSearch =
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.table.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.waiter.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesPayment =
        paymentFilter === "all" || order.payments.some((p) => p.method === paymentFilter);

      // Convert order relative dates (Bugün/Dün) to real timestamps for comparison
      let orderDateObj = new Date();
      orderDateObj.setHours(0, 0, 0, 0);
      if (order.date === "Bugün") {
        // Today
      } else if (order.date === "Dün") {
        orderDateObj.setDate(orderDateObj.getDate() - 1);
      }

      let matchesDate = false;
      if (startDate && endDate) {
        matchesDate = orderDateObj >= startDate && orderDateObj <= endDate;
      } else if (startDate) {
        matchesDate = orderDateObj.getTime() === startDate.getTime();
      } else {
        matchesDate = true;
      }

      return matchesSearch && matchesPayment && matchesDate;
    });
  }, [searchTerm, paymentFilter, startDate, endDate]);

  // Selected Order
  const selectedOrder = useMemo(() => {
    return MOCK_ORDERS.find((o) => o.id === selectedOrderId) || MOCK_ORDERS[0];
  }, [selectedOrderId]);

  // Stats calculation inline
  const stats = useMemo(() => {
    const activeOrders = MOCK_ORDERS.filter((o) => o.status === "completed");
    const totalRevenue = activeOrders.reduce((sum, o) => sum + o.total, 0);
    return {
      totalRevenue,
      count: activeOrders.length,
    };
  }, []);

  return (
    <div className="flex h-full flex-col bg-white font-sans antialiased text-gray-900 overflow-hidden relative">
      {/* Backdrop for closing datepicker */}
      {isDatePickerOpen && (
        <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setIsDatePickerOpen(false)} />
      )}

      {/* Sleek Top Header */}
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-gray-150 px-6">
        <div className="flex items-center gap-3">
          <Receipt className="size-5 text-gray-500" />
          <div>
            <h1 className="text-base font-bold text-gray-950">Adisyon Geçmişi</h1>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
              Günlük Ciro: {formatTRY(stats.totalRevenue)} • {stats.count} Satış
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Minimalist Search Bar */}
          <div className="relative flex items-center bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 focus-within:bg-white focus-within:border-gray-300 transition-all w-48 sm:w-64">
            <Search className="size-3.5 text-gray-400 mr-1.5 shrink-0" />
            <input
              type="text"
              placeholder="Adisyon, masa veya garson ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs font-semibold bg-transparent border-none outline-none text-gray-800 placeholder-gray-400"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="text-gray-400 hover:text-gray-600">
                <X className="size-3" />
              </button>
            )}
          </div>

          <button className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-all cursor-pointer">
            <FileSpreadsheet className="size-3.5 text-emerald-500" />
            <span>Rapor Al</span>
          </button>
        </div>
      </header>

      {/* Main Split Layout */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        
        {/* Left Side: Orders Flat List */}
        <div className="w-[360px] shrink-0 border-r border-gray-150 flex flex-col min-h-0 bg-gray-50/30">
          
          {/* Quick Filters */}
          <div className="p-3 border-b border-gray-150 flex gap-2 shrink-0 bg-white relative z-40">
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="w-1/2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-700 focus:outline-none cursor-pointer"
            >
              <option value="all">Tüm Ödemeler</option>
              <option value="Kredi Kartı">Kredi Kartı</option>
              <option value="Nakit">Nakit</option>
              <option value="Yemek Kartı">Yemek Kartı</option>
            </select>

            <button
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className="w-1/2 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-100 transition-all text-left cursor-pointer"
            >
              <span className="truncate">{dateRangeLabel}</span>
              <Calendar className="size-3.5 text-gray-400 shrink-0 ml-1" />
            </button>

            {/* Custom Range Calendar Popover */}
            {isDatePickerOpen && (
              <div className="absolute left-3 right-3 top-full mt-2 z-50 bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-150">
                {/* Nav Header */}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
                    }}
                    className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all cursor-pointer"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <span className="text-xs font-black text-gray-800 uppercase tracking-wider">
                    {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
                    }}
                    className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all cursor-pointer"
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>

                {/* Day Names */}
                <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-black text-gray-400 uppercase tracking-wider">
                  <span>Pt</span>
                  <span>Sa</span>
                  <span>Ça</span>
                  <span>Pe</span>
                  <span>Cu</span>
                  <span>Ct</span>
                  <span>Pz</span>
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} />;
                    const dayStr = day.toISOString().split("T")[0];
                    const isToday = new Date().toISOString().split("T")[0] === dayStr;

                    const isStart = startDate && day.getTime() === startDate.getTime();
                    const isEnd = endDate && day.getTime() === endDate.getTime();
                    const isRange = startDate && endDate && day > startDate && day < endDate;

                    return (
                      <button
                        key={dayStr}
                        type="button"
                        onClick={() => handleDayClick(day)}
                        className={`flex aspect-square items-center justify-center rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                          isStart || isEnd
                            ? "bg-cyan-600 text-white font-black"
                            : isRange
                            ? "bg-cyan-50 text-cyan-700 font-bold rounded-none"
                            : isToday
                            ? "bg-gray-50 text-gray-900 border border-gray-200 font-bold"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Simple flat items list */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {filteredOrders.map((order) => {
              const isSelected = order.id === selectedOrderId;
              return (
                <button
                  key={order.id}
                  onClick={() => setSelectedOrderId(order.id)}
                  className={`w-full flex items-center justify-between p-4 transition-all hover:bg-gray-50 text-left ${
                    isSelected ? "bg-slate-100/70 border-r-2 border-slate-900" : ""
                  }`}
                >
                  <div className="min-w-0 pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-gray-900">#{order.id}</span>
                      <span className="text-[10px] font-extrabold text-gray-400 bg-white border border-gray-200 px-1.5 py-0.2 rounded uppercase">
                        Masa {order.table}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 font-semibold mt-1 flex items-center gap-1">
                      <span>{order.waiter}</span>
                      <span>•</span>
                      <span>{order.date}, {order.time}</span>
                    </p>
                  </div>

                  <div className="text-right shrink-0 flex items-center gap-2">
                    <div>
                      <span className="block text-xs font-black text-gray-950">
                        {formatTRY(order.total)}
                      </span>
                      <span className={`text-[8px] font-black uppercase tracking-wider mt-0.5 block ${
                        order.status === "refunded" ? "text-rose-500" : "text-emerald-500"
                      }`}>
                        {order.status === "refunded" ? "İADE" : "TAMAM"}
                      </span>
                    </div>
                    <ChevronRight className="size-3 text-gray-300" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Side: Elegant detail panel */}
        <div className="flex-1 flex flex-col min-h-0 bg-white">
          {selectedOrder ? (
            <>
              {/* Detail Header Info */}
              <div className="p-6 border-b border-gray-150 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-base font-bold text-gray-950 flex items-center gap-2">
                    <span>Adisyon Hesabı: #{selectedOrder.id}</span>
                    <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full border ${
                      selectedOrder.status === "refunded"
                        ? "bg-rose-50 text-rose-600 border-rose-100"
                        : "bg-emerald-50 text-emerald-600 border-emerald-100"
                    }`}>
                      {selectedOrder.status === "refunded" ? "İade Edildi" : "Kapatıldı"}
                    </span>
                  </h2>
                  <p className="text-xs font-semibold text-gray-400 mt-1">
                    Masa {selectedOrder.table} • {selectedOrder.guestCount} Kişi • Açılış/Kapanış: {selectedOrder.time}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button className="flex items-center gap-1 rounded-lg border border-gray-200 hover:bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-all cursor-pointer">
                    <Printer className="size-3.5" />
                    <span>Yazdır</span>
                  </button>
                  {selectedOrder.status !== "refunded" && (
                    <button className="flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer">
                      <RotateCcw className="size-3.5" />
                      <span>İade Yap</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Order detail grid */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                
                {/* 1. Products List */}
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Sipariş Edilen Ürünler</h3>
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-100 text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                        <th className="pb-2">Ürün Adı</th>
                        <th className="pb-2 text-center w-16">Miktar</th>
                        <th className="pb-2 text-right w-24">Fiyat</th>
                        <th className="pb-2 text-right w-28">Toplam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {selectedOrder.items.map((item, idx) => (
                        <tr key={idx} className="text-xs text-gray-800">
                          <td className="py-3 font-semibold">
                            {item.name}
                            {item.note && (
                              <span className="block text-[10px] text-rose-500 font-semibold mt-0.5">
                                * Not: {item.note}
                              </span>
                            )}
                          </td>
                          <td className="py-3 text-center font-bold text-gray-900">{item.quantity}</td>
                          <td className="py-3 text-right text-gray-500 font-medium">{formatTRY(item.price)}</td>
                          <td className="py-3 text-right font-bold text-gray-950">{formatTRY(item.price * item.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 2. Cancelled Products Log */}
                {selectedOrder.cancelledItems.length > 0 && (
                  <div className="pt-2">
                    <h3 className="text-xs font-bold text-rose-500 uppercase tracking-wider mb-2">Silinen Ürün Kayıtları (Audit Log)</h3>
                    <table className="w-full text-left border-collapse text-[11px]">
                      <thead>
                        <tr className="border-b border-gray-100 text-[9px] text-gray-400 font-bold uppercase tracking-wider">
                          <th className="pb-2">Ürün</th>
                          <th className="pb-2 text-center w-12">Adet</th>
                          <th className="pb-2 text-right w-20">Tutar</th>
                          <th className="pb-2 w-28">İptal Eden</th>
                          <th className="pb-2">Neden</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 text-gray-600">
                        {selectedOrder.cancelledItems.map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2.5 font-semibold text-rose-800">{item.name}</td>
                            <td className="py-2.5 text-center font-bold">{item.quantity}</td>
                            <td className="py-2.5 text-right font-bold">{formatTRY(item.price * item.quantity)}</td>
                            <td className="py-2.5 text-gray-700">{item.cancelledBy}</td>
                            <td className="py-2.5 italic text-gray-400 font-medium">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 3. Settle Payment Splits */}
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">Tahsilat Detayları</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedOrder.payments.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-3 border border-gray-200 rounded-lg px-3.5 py-2 text-xs bg-gray-50/50">
                        <span className="font-semibold text-gray-500">{p.method}:</span>
                        <span className="font-bold text-gray-900">{formatTRY(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4. Audit Log details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-1.5">
                      Operasyon Geçmişi
                    </h3>
                    <div className="space-y-1.5 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Adisyonu Açan</span>
                        <span className="font-semibold text-gray-900">{selectedOrder.waiter}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Hesabı Kapatan</span>
                        <span className="font-semibold text-gray-900">{selectedOrder.cashier}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Adisyon Fiş No</span>
                        <span className="font-semibold text-gray-900 font-mono">{selectedOrder.fiscalInfo.receiptNo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mali Gün Z-No</span>
                        <span className="font-semibold text-gray-900">Z-{selectedOrder.fiscalInfo.zNo}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-1.5">
                      Hesap Matematik Detayları
                    </h3>
                    <div className="space-y-1.5 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Ara Toplam</span>
                        <span className="font-semibold text-gray-900">
                          {formatTRY(selectedOrder.items.reduce((sum, i) => sum + i.price * i.quantity, 0))}
                        </span>
                      </div>
                      {selectedOrder.coverCharge > 0 && (
                        <div className="flex justify-between">
                          <span>Kuver</span>
                          <span className="font-semibold text-gray-900">+{formatTRY(selectedOrder.coverCharge)}</span>
                        </div>
                      )}
                      {selectedOrder.serviceCharge > 0 && (
                        <div className="flex justify-between">
                          <span>Servis Bedeli</span>
                          <span className="font-semibold text-gray-900">+{formatTRY(selectedOrder.serviceCharge)}</span>
                        </div>
                      )}
                      {selectedOrder.discount > 0 && (
                        <div className="flex justify-between text-rose-600">
                          <span>Uygulanan İndirim ({selectedOrder.discountReason || "Manuel"})</span>
                          <span className="font-bold">-{formatTRY(selectedOrder.discount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-gray-400 text-[11px]">
                        <span>Dahil KDV (%{selectedOrder.taxRate})</span>
                        <span>{formatTRY(selectedOrder.tax)}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Bottom Summary Bar */}
              <div className="p-6 border-t border-gray-150 bg-gray-50 flex items-center justify-between shrink-0">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tahsil Edilen Toplam Tutar</span>
                <span className="text-xl font-black text-gray-950">{formatTRY(selectedOrder.total)}</span>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <Receipt className="size-10 stroke-1" />
              <p className="text-xs font-bold mt-2">Detayları görmek için sol taraftan bir adisyon seçin</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
